import { useSyncExternalStore } from 'react';
import type { PassengerRideView, RideStatus } from '@limuzin/passenger';

/**
 * «Звуки» — the three moments of a ride the passenger hears, and what each sounds
 * like in this browser: the chime the app came with, nothing, or a file of their own
 * (ADR 0031). A port of `event_sounds.dart` and the rider app's `RideAlerts.moment`.
 *
 * Kept in this browser, as the phone keeps it on the handset: the choice in
 * `localStorage`, and a picked file in IndexedDB, which is the one store here that
 * holds a file. Nothing is sent anywhere.
 *
 * A page can only be heard while it is running. With no tab alive the browser's own
 * notification is what says «Водитель выехал к вам» (ADR 0027), in the sound the
 * system gives it — a page has no say in that one.
 */

/** One thing the app makes a sound for, as «Настройки» lists it. */
export interface SoundEvent {
  /** What the choice is kept under; never changes once shipped. */
  key: string;
  title: string;
  /** The chime the app came with, made by `scripts/ride-sounds.py`. */
  src: string;
}

/** A driver took the ride: «Ищем машину» has become «Заказ принят». */
export const ACCEPTED: SoundEvent = {
  key: 'accepted',
  title: 'Заказ принят',
  src: '/sounds/zakaz_prinyat.mp3',
};
/** A предзаказ's driver set off — the one a push says too. */
export const SET_OFF: SoundEvent = {
  key: 'setOff',
  title: 'Водитель выехал',
  src: '/sounds/voditel_vyehal.mp3',
};
/** The car is at the pickup. */
export const ARRIVED: SoundEvent = {
  key: 'arrived',
  title: 'Машина на месте',
  src: '/sounds/na_meste.mp3',
};

/**
 * Least pressing first, so that of two heard at once the later one — the one that
 * is played — is the one the passenger has to act on.
 */
export const SOUNDS: readonly SoundEvent[] = [ACCEPTED, SET_OFF, ARRIVED];

// ------------------------------------------------------------- what is heard

const LIVE: ReadonlySet<RideStatus> = new Set([
  'SCHEDULED', 'SEARCHING', 'ACCEPTED', 'EN_ROUTE', 'ON_SITE', 'IN_PROGRESS',
]);
const DEPARTED: ReadonlySet<RideStatus> = new Set(['EN_ROUTE', 'ON_SITE', 'IN_PROGRESS']);
const NO_DRIVER: ReadonlySet<RideStatus> = new Set(['SCHEDULED', 'SEARCHING']);

type Seen = Pick<PassengerRideView, 'id' | 'status' | 'scheduledFor'>;

/**
 * Which of the three moments a ride passed in going from `was` to where it stands,
 * if any.
 *
 * A «сейчас» ride is taken straight into «Подача» (ADR 0013), and that is «Заказ
 * принят»: it is what the screen says, and nobody set off a second time. «Водитель
 * выехал» is a предзаказ's alone, as it is in the notification.
 */
export function moment(was: RideStatus, ride: Omit<Seen, 'id'>): SoundEvent | null {
  const now = ride.status;
  if (now === was || !LIVE.has(was)) return null;
  if (now === 'ON_SITE') return was === 'IN_PROGRESS' ? null : ARRIVED;
  if (ride.scheduledFor !== null && now === 'EN_ROUTE' && !DEPARTED.has(was)) return SET_OFF;
  const taken = now === 'ACCEPTED' || now === 'EN_ROUTE';
  return taken && NO_DRIVER.has(was) ? ACCEPTED : null;
}

/**
 * Watches the ride list go past and says which moments it shows.
 *
 * Against what this app last knew, not against anything the server sends: «changed»
 * here means changed since the passenger could last have seen it. The first list is
 * the app finding out where everything already stands, and a ride seen for the first
 * time was placed a moment ago — neither is news.
 */
export class RideSounds {
  private seen = new Map<string, RideStatus>();
  private caughtUp = false;

  /** Takes in a list the server has just given, and answers with what to play. */
  saw(rides: readonly Seen[]): SoundEvent[] {
    const before = this.seen;
    this.seen = new Map(rides.map((ride) => [ride.id, ride.status]));
    if (!this.caughtUp) {
      this.caughtUp = true;
      return [];
    }
    const heard = new Set<SoundEvent>();
    for (const ride of rides) {
      const was = before.get(ride.id);
      const event = was === undefined ? null : moment(was, ride);
      if (event) heard.add(event);
    }
    return SOUNDS.filter((event) => heard.has(event));
  }

  /** The session ended: whoever signs in next starts from their own first list. */
  reset(): void {
    this.seen = new Map();
    this.caughtUp = false;
  }
}

// ------------------------------------------------------------------ the choice

export type SoundChoice =
  | { kind: 'standard' }
  | { kind: 'none' }
  /** A file the passenger picked, by the name they know it by. */
  | { kind: 'own'; name: string };

const STANDARD: SoundChoice = { kind: 'standard' };
const KEY = 'limuzin.customer.sounds';
const listeners = new Set<() => void>();

function read(): Record<string, SoundChoice> {
  const kept: Record<string, SoundChoice> = {};
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(KEY) ?? '{}');
    if (typeof stored !== 'object' || stored === null) return kept;
    for (const [key, value] of Object.entries(stored as Record<string, unknown>)) {
      if (typeof value !== 'object' || value === null) continue;
      const { kind, name } = value as { kind?: unknown; name?: unknown };
      if (kind === 'none') kept[key] = { kind };
      else if (kind === 'own' && typeof name === 'string') kept[key] = { kind, name };
    }
  } catch {
    // No store, or one that holds something this version does not understand:
    // every moment sounds as the app shipped it.
  }
  return kept;
}

let choices: Record<string, SoundChoice> | null = null;
const held = () => (choices ??= read());

/** What `event` sounds like: «Стандартный» for one nobody has chosen for. */
export function choiceOf(event: SoundEvent): SoundChoice {
  return held()[event.key] ?? STANDARD;
}

function choose(event: SoundEvent, choice: SoundChoice): void {
  const next = { ...held() };
  if (choice.kind === 'standard') delete next[event.key];
  else next[event.key] = choice;
  choices = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage refused: the choice lasts as long as the tab.
  }
  listeners.forEach((heard) => heard());
}

export function useSoundChoice(event: SoundEvent): SoundChoice {
  return useSyncExternalStore(
    (heard) => {
      listeners.add(heard);
      return () => listeners.delete(heard);
    },
    () => choiceOf(event),
  );
}

/** What the row under «Звуки» says of a choice. */
export function choiceHint(choice: SoundChoice): string {
  switch (choice.kind) {
    case 'standard':
      return 'Звук: стандартный';
    case 'none':
      return 'Без звука';
    case 'own':
      return `Звук: ${choice.name}`;
  }
}

export function chooseStandard(event: SoundEvent): void {
  choose(event, STANDARD);
  void forget(event);
}

export function chooseNone(event: SoundEvent): void {
  choose(event, { kind: 'none' });
  void forget(event);
}

/** Why a picked file was not taken, in the words the sheet says it in. */
export class SoundRefused extends Error {}

/** Ten megabytes: a long ringtone at any bitrate, and far short of an album. */
export const MAX_BYTES = 10 * 1024 * 1024;

/**
 * «Свой файл…»: takes `file` as the sound of `event`. Rejects with `SoundRefused`
 * where the file would not do, and the choice stands as it was.
 *
 * Asked of the browser's player rather than of the file's name: a picker offers
 * whatever the device calls audio, and only the player knows what it can open. Found
 * out here, in front of the person choosing, and not when the car arrives in
 * silence.
 */
export async function adoptSound(event: SoundEvent, file: File): Promise<void> {
  if (file.size > MAX_BYTES) throw new SoundRefused('Файл больше 10 МБ. Выберите звук покороче.');
  if (file.size === 0 || !(await opens(file))) {
    throw new SoundRefused('Не получилось открыть этот файл как звук.');
  }
  try {
    await keep(event, file);
  } catch {
    // Private browsing, or a full disk.
    throw new SoundRefused('Браузер не дал сохранить файл.');
  }
  choose(event, { kind: 'own', name: file.name });
}

function opens(file: Blob): Promise<boolean> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const probe = new Audio();
    const answer = (ok: boolean) => {
      clearTimeout(waited);
      probe.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(ok);
    };
    const waited = setTimeout(() => answer(false), 8000);
    probe.onloadedmetadata = () => answer(true);
    probe.onerror = () => answer(false);
    probe.preload = 'metadata';
    probe.src = url;
  });
}

// ------------------------------------------------------------ the picked files

const FILES = 'files';
/** The files' addresses for the player, made once each and let go when replaced. */
const urls = new Map<string, string>();

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const opening = indexedDB.open(KEY, 1);
    opening.onupgradeneeded = () => opening.result.createObjectStore(FILES);
    opening.onsuccess = () => resolve(opening.result);
    opening.onerror = () => reject(opening.error);
  });
}

async function within<T>(
  mode: IDBTransactionMode,
  work: (files: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await database();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(FILES, mode);
      const request = work(transaction.objectStore(FILES));
      transaction.oncomplete = () => resolve(request.result);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
}

function release(event: SoundEvent): void {
  const url = urls.get(event.key);
  if (url) URL.revokeObjectURL(url);
  urls.delete(event.key);
}

async function keep(event: SoundEvent, file: Blob): Promise<void> {
  await within('readwrite', (files) => files.put(file, event.key));
  release(event);
}

async function forget(event: SoundEvent): Promise<void> {
  release(event);
  try {
    await within('readwrite', (files) => files.delete(event.key));
  } catch {
    // Nothing kept, or nowhere to keep it.
  }
}

/** Where the player finds the file picked for `event`, or null where it has gone. */
async function ownUrl(event: SoundEvent): Promise<string | null> {
  const known = urls.get(event.key);
  if (known) return known;
  try {
    const file = await within<Blob | undefined>('readonly', (files) => files.get(event.key));
    if (!file) return null;
    const url = URL.createObjectURL(file);
    urls.set(event.key, url);
    return url;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------ the voice

/** The longest any sound runs. A picked file can be a whole song. */
const LIMIT = 30_000;

let voice: HTMLAudioElement | null = null;
let turn = 0;
let cut: ReturnType<typeof setTimeout> | undefined;

/**
 * Plays what the passenger chose for `event`, from the start, in place of whatever
 * was playing. Quiet about failing: a browser that will not play — no gesture yet, a
 * file it has lost — is not worth breaking a ride over. A picked file that has gone
 * sounds as the standard one: the moment still happened.
 */
export function play(event: SoundEvent): void {
  if (typeof Audio === 'undefined') return;
  const choice = choiceOf(event);
  if (choice.kind === 'none') return;
  const mine = ++turn;
  void (choice.kind === 'own' ? ownUrl(event) : Promise.resolve(null)).then((own) => {
    // Overtaken while the file was being read, by a later sound or by `hush`.
    if (mine !== turn) return;
    const audio = (voice ??= new Audio());
    audio.muted = false;
    audio.src = own ?? event.src;
    audio.currentTime = 0;
    clearTimeout(cut);
    cut = setTimeout(hush, LIMIT);
    void audio.play().catch(() => {});
  });
}

/** Stops whatever is playing, and whatever was about to. */
export function hush(): void {
  turn++;
  clearTimeout(cut);
  voice?.pause();
}

/**
 * Lets the voice be heard later without a press of its own. Safari plays a sound
 * only from something the person just pressed — but an element that has played once
 * from a press may play again by itself. So the first press anywhere in the app
 * plays the voice muted for an instant, and «Машина на месте» can then sound with
 * the phone lying on the table. Returns the way to stop waiting for that press.
 */
export function primeVoice(): () => void {
  if (typeof Audio === 'undefined') return () => {};
  const prime = () => {
    stop();
    const audio = (voice ??= new Audio());
    // Sounding already, which this very press has just allowed.
    if (!audio.paused) return;
    const mine = turn;
    audio.muted = true;
    audio.src = ACCEPTED.src;
    void audio
      .play()
      .then(() => {
        if (mine === turn) audio.pause();
      })
      .catch(() => {})
      .finally(() => {
        if (mine === turn) audio.muted = false;
      });
  };
  const stop = () => {
    document.removeEventListener('pointerdown', prime);
    document.removeEventListener('keydown', prime);
  };
  document.addEventListener('pointerdown', prime);
  document.addEventListener('keydown', prime);
  return stop;
}
